import { ChevronDown, LogOut, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { AuthLanguageSwitcher } from '@features/auth/components/AuthLanguageSwitcher'
import { useLogout } from '@services/auth/mutations/useLogout'
import { userStorage } from '@services/utils/local-storage'

import { NavUser, settingsNavItem } from './navConfig'

function initials(user?: NavUser) {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
  }
  return user?.email?.[0]?.toUpperCase() ?? '?'
}

export function ProfileMenu({ user }: { user?: NavUser }) {
  const { t } = useTranslation('layout')
  const { mutate: logout } = useLogout()

  const name =
    user?.first_name || user?.last_name
      ? `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
      : user?.email

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full p-1 pr-2 transition-colors hover:bg-ink/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Avatar>
          <AvatarFallback>{initials(user)}</AvatarFallback>
        </Avatar>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
          <span className="truncate text-sm font-semibold text-ink">
            {name}
          </span>
          <span className="truncate text-xs font-normal text-muted-foreground">
            {user?.email}
          </span>
          {user?.role && (
            <span className="mt-1 inline-flex w-fit rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {t(`${user.role}`)}
            </span>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Language lives in the top bar on desktop; surface it here on mobile. */}
        <div className="px-1.5 py-1.5 sm:hidden">
          <AuthLanguageSwitcher className="w-full justify-center" />
        </div>
        <DropdownMenuSeparator className="sm:hidden" />

        <DropdownMenuItem asChild>
          <Link to={settingsNavItem.key}>
            <Settings />
            {t('navigation.settings')}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => logout({ refresh: userStorage.getRefresh() || '' })}
        >
          <LogOut />
          {t('navigation.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
